import _ from "lodash";
import utils from "./utils";
import constant from "./constant";
import { Geolocation } from '@capacitor/geolocation';

const calculate_distance = (lat1, lon1, lat2, lon2) => {
  const toRadians = (degree) => (degree * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
    Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const getCurrentLocation = async () => {
  try {
    const permission = await Geolocation.requestPermissions();

    if (permission.location !== "granted") {
      throw new Error("Location permission not granted");
    }

    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 10000,
    });

    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;

    const currentLatLng = utils._retrieveData(constant.CURRENT_LAT_LNG);

    if (currentLatLng) {
      const distance = calculate_distance(
        latitude,
        longitude,
        currentLatLng?.latitude,
        currentLatLng?.longitude
      );

      if (distance <= 50) {
        const cachedLocation = utils._retrieveData(
          constant.CURRENT_LOCATION
        );

        if (!_.isEmpty(cachedLocation)) {
          return cachedLocation;
        }
      }
    }

    utils._storeData(constant.CURRENT_LAT_LNG, { latitude, longitude });

    if (!window.google || !window.google.maps) {
      throw new Error("Google Maps not loaded");
    }

    const geocoder = new window.google.maps.Geocoder();
    const latlng = new window.google.maps.LatLng(latitude, longitude);

    const { results } = await geocoder.geocode({ location: latlng });

    if (!results || results.length === 0) {
      throw new Error("No results found for the provided coordinates.");
    }

    const selectedLocation = results[0];
    const components = selectedLocation.address_components;

    const findComponent = (type) =>
      _.get(
        _.find(components, (comp) => _.includes(comp.types, type)),
        "long_name",
        "Unknown"
      );

    const locationData = {
      address_name: findComponent("political"),
      city: findComponent("locality"),
      state: findComponent("administrative_area_level_1"),
      pincode:
        findComponent("postal_code") === "Unknown"
          ? "00000"
          : findComponent("postal_code"),
      coordinates: {
        latitude:
          typeof selectedLocation.geometry.location.lat === "function"
            ? selectedLocation.geometry.location.lat()
            : selectedLocation.geometry.location.lat,
        longitude:
          typeof selectedLocation.geometry.location.lng === "function"
            ? selectedLocation.geometry.location.lng()
            : selectedLocation.geometry.location.lng,
      },
      formatted_address:
        selectedLocation.formatted_address || "Unknown",
    };

    utils._storeData(constant.CURRENT_LOCATION, locationData);

    return locationData;
  } catch (error) {
    throw new Error(
      error?.message || "Error fetching current location"
    );
  }
};
